import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CameraName from './CameraName'
import CameraColorPicker from './CameraColorPicker'
import CalculatorInput from 'components/CalculatorInput'
import CalculatorSelect from 'components/CalculatorSelect'
import AngleOfViewContainer from 'components/AngleOfViewContainer'
import ResolutionContainer from 'components/ResolutionContainer'
import FocalLengthContainer from 'components/FocalLengthContainer'
import ImagerSizeContainer from 'components/ImagerSizeContainer'
import Switch from 'react-switch'
import getCurrentCamera from 'helpers/cameras/getCurrentCamera'
import { UNIT_OF_MEASUREMENT_OPTIONS, RESOLUTIONS, METRIC, IMPERIAL, IMAGER_SIZES, COLORS, COPY_ICON_STACK } from 'helpers/globals'
import fovModeHelper from 'helpers/fovModeHelper'
import { updateCalculatorAction } from 'actions/calculatorActions'
import { updateFixedLensAction } from 'actions/multiImagerActions'
import { zoomToSingleCameraAction } from 'actions/mapActions'
import { updateCurrentEntityAction, checkOfflineLimitsAction } from 'actions/applicationActions'
import { notificationAction } from 'actions/notificationActions'
import { updateUiAction, updateUnitAction } from 'actions/uiActions'
import { changeCameraAction } from 'actions/cameraActions'
import sortItems from 'helpers/sortItems'

function mapStateToProps({ presentationValues, cameras, ui: { showCameraBlindspot, showAnalyticZones, isImperial } }) {
  const currentCamera = getCurrentCamera(cameras)
  return {
    presentationValues,
    currentCamera,
    isImperial,
    resolution: currentCamera && `${currentCamera.horizontalResolution} x ${currentCamera.verticalResolution}`,
    cameras,
    multiImager: currentCamera && cameras.find(c => c.id === currentCamera.imagerId),
    showCameraBlindspot,
    showAnalyticZones
  }
}

const focalLengthDisplay = (fl, aov) => aov === 360 ? '0.0' : fl

const Label = ({ id, labelText, checked, onChange, disabled, classes }) => (
  <div>
    <label htmlFor={id} className={classes.label}>
      <span>
        { labelText }
      </span>
      <Switch
        checked={!disabled && checked}
        onChange={onChange}
        onColor='#5CA5E4'
        onHandleColor={COLORS.primary}
        handleDiameter={25}
        uncheckedIcon={false}
        checkedIcon={false}
        boxShadow='0px 1px 5px rgba(0, 0, 0, 0.6)'
        activeBoxShadow='0px 0px 1px 10px rgba(0, 0, 0, 0.2)'
        height={17}
        width={40}
        className='react-switch'
        id={id}
        disabled={disabled}
      />
    </label>
  </div>
)

class CameraConfig extends React.Component {
  renderLensList = _ => {
    const { currentCamera: { lensIds }, cameras, changeCameraAction, zoomToSingleCameraAction, classes } = this.props
    const lenses = lensIds.map(lensId => cameras.find(c => c.id === lensId))

    return (
      <div className={classes.lensContainer}>
        <span>Imagers:</span>
        <div className={classes.lensWrapper}>
          {
            sortItems(lenses).map(lens => (
              <a onClick={_ => {
                  changeCameraAction(lens.id)
                  zoomToSingleCameraAction(lens)
                }}
                key={'lens' + lens.id}
              >
                {lens.name} <FontAwesomeIcon icon='arrow-right' className={classes.rightArrow} />
              </a>
            ))
          }
        </div>
      </div>
    )
  }

  render() {
    const { currentCamera, resolution, presentationValues: { fieldOfView, angleOfView, ppf, distance, focalLength }, multiImager, openSelector,
      showCameraBlindspot, showAnalyticZones, updateCalculatorAction, updateFixedLensAction, zoomToSingleCameraAction, updateCurrentEntityAction, isImperial,
      notificationAction, updateUiAction, updateUnitAction, checkOfflineLimitsAction, classes } = this.props

    if (!currentCamera) return null

    const { id, model, fovMode, imagerSize, locked, isMultiImager, imagerId, isFixedImager, horizontalAngleOfView } = currentCamera
    const callback = isFixedImager ? updateFixedLensAction : updateCalculatorAction

    return (
      <div className={classes.cameraConfig}>
        <div className={classes.header}>
          <CameraName { ...{ currentCamera, updateCalculatorAction, classes } } />
        </div>
        <div className={classes.body}>
          <div className={classes.buttonsContainer}>
            <div
              onClick={_ => {
                if ((imagerId && multiImager.locked) || (!imagerId && locked)) notificationAction('Cannot delete locked camera')
                else updateUiAction({ modal: 'DeleteCamera' })
              }}
              className={classes.deleteButton}
            >
              <FontAwesomeIcon icon={['far', 'trash-alt']} />
            </div>
            <CameraColorPicker currentCamera={currentCamera} bottomBar />
            { !imagerId &&
              <div onClick={_ => isMultiImager ? checkOfflineLimitsAction('copyMultiImager', [id]) : checkOfflineLimitsAction('copyCamera', [id])}>
                {COPY_ICON_STACK}
              </div>
            }
            <div onClick={_ => {
                zoomToSingleCameraAction(currentCamera)
                updateCurrentEntityAction({ id, type: 'camera' })
              }} >
              <FontAwesomeIcon icon='map-marker-alt' />
            </div>
            <div onClick={_ => updateCalculatorAction(!locked, 'locked', id)}>
              <FontAwesomeIcon icon={locked ? 'lock' : 'unlock'} />
            </div>
          </div>
          <div className={classes.row}>
            <span>Model:</span>
            <span className={classes.modelName} onClick={openSelector}>{ model ? model.name : 'Generic Camera' }</span>
          </div>
          { isMultiImager
            ? this.renderLensList()
            : <div className={classes.inputs}>
                <ResolutionContainer
                  currentCamera={currentCamera}
                  >
                  <CalculatorSelect
                    currentCamera={currentCamera}
                    label='Resolution'
                    titleTag='Horizontal Resolution'
                    selectedValue={resolution}
                    values={RESOLUTIONS}
                    fnKey={'resolutions'}
                    callback={callback}
                    labelClass='extraWideLabel'
                  />
                </ResolutionContainer>
                <CalculatorInput
                  currentCamera={currentCamera}
                  label={isImperial ? 'PPF' : 'PPM'}
                  titleTag={`Pixels per ${isImperial ? 'Foot' : 'Meter'}`}
                  value={ppf.value}
                  fnKey={'ppf'}
                  callback={callback}
                  labelClass='extraWideLabel'
                  rawValue={currentCamera.ppf}
                />
                <CalculatorInput
                  currentCamera={currentCamera}
                  label={'Distance'}
                  value={distance.value}
                  fnKey={'distance'}
                  unit={isImperial ? 'ft' : 'm'}
                  callback={callback}
                  labelClass='extraWideLabel'
                  rawValue={currentCamera.distance}
                />
                <CalculatorInput
                  currentCamera={currentCamera}
                  label={'Width'}
                  titleTag='Horizontal Width'
                  value={fieldOfView.value}
                  fnKey={`${fovModeHelper(fovMode)}FieldOfView`}
                  unit={isImperial ? 'ft' : 'm'}
                  callback={callback}
                  labelClass='extraWideLabel'
                  rawValue={currentCamera[`${fovModeHelper(fovMode)}FieldOfView`]}
                />
                <AngleOfViewContainer
                  statelessComponent={
                    <CalculatorInput
                      currentCamera={currentCamera}
                      label='HAoV'
                      titleTag='Horizontal Angle of View'
                      value={angleOfView.value}
                      fnKey={`${fovModeHelper(fovMode)}AngleOfView`}
                      unit='°'
                      callback={callback}
                      labelClass='extraWideLabel'
                      rawValue={currentCamera[`${fovModeHelper(fovMode)}AngleOfView`]}
                    />
                  }
                  currentCamera={currentCamera}
                  callback={callback}
                />
                <CalculatorSelect
                  currentCamera={currentCamera}
                  label='Unit'
                  titleTag='Unit of Measurement'
                  selectedValue={isImperial ? IMPERIAL : METRIC}
                  values={UNIT_OF_MEASUREMENT_OPTIONS}
                  fnKey={'isImperial'}
                  callback={(value, _, __) => updateUnitAction(value === IMPERIAL)}
                  id={id}
                  labelClass='extraWideLabel'
                />
                <ImagerSizeContainer
                  statelessComponent={
                    <CalculatorSelect
                      currentCamera={currentCamera}
                      label='Imager'
                      titleTag='Imager Size'
                      selectedValue={imagerSize}
                      values={IMAGER_SIZES}
                      fnKey='imagerSize'
                      callback={callback}
                      labelClass='extraWideLabel'
                    />
                  }
                  currentCamera={currentCamera}
                />
                <FocalLengthContainer
                  currentCamera={currentCamera}
                  >
                  <CalculatorInput
                    currentCamera={currentCamera}
                    label='Focal Length'
                    value={focalLengthDisplay(focalLength.value, horizontalAngleOfView)}
                    fnKey={'focalLength'}
                    unit={'mm'}
                    callback={callback}
                    labelClass='extraWideLabel'
                    rawValue={focalLengthDisplay(focalLength.value, horizontalAngleOfView)}
                  />
                </FocalLengthContainer>
                <Label
                  id='showBlindspotSwitch'
                  labelText='Show camera blind spots'
                  checked={showCameraBlindspot}
                  disabled={horizontalAngleOfView >= 180}
                  onChange={checked => updateUiAction({ showCameraBlindspot: checked })}
                  classes={classes}
                />
                <Label
                  id='showAnalyticZonesSwitch'
                  labelText={`Show ${isImperial ? 'PPF' : 'PPM'} zones`}
                  checked={showAnalyticZones}
                  disabled={false}
                  onChange={checked => updateUiAction({ showAnalyticZones: checked })}
                  classes={classes}
                />
              </div>
          }
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, { updateCalculatorAction, updateFixedLensAction, zoomToSingleCameraAction, updateCurrentEntityAction,
  notificationAction, updateUiAction, updateUnitAction, changeCameraAction, checkOfflineLimitsAction })(injectSheet(style)(CameraConfig))



// WEBPACK FOOTER //
// ./src/components/BottomBar/CameraConfig/index.js